import { createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Camera, Check, Link as LinkIcon, MessageCircle, Share2 } from "lucide-solid";

import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useI18n } from "@/providers/i18n-provider";

interface SharePost {
  id: string;
  authorName: string | null;
  caption: string | null;
  cover: string | null;
}

/**
 * 🔗 Menú de compartir (bottom sheet) + previsualización de historia. Copiar
 * enlace, compartir nativo (Web Share API) y una preview 9:16 estilo «historia»
 * con la foto + marca FamiDex. Traducción de `menu_de_compartir` +
 * `previsualizacion_de_historia_*`.
 */
export function ShareSheet(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  post: SharePost;
  shareUrl: string;
}) {
  const { t } = useI18n();
  const [copied, setCopied] = createSignal(false);
  const [showStory, setShowStory] = createSignal(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard?.writeText(props.shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard bloqueado: no-op */
    }
  };

  const nativeShare = async () => {
    const shareData = {
      title: t(msg`FamiDex`),
      text: props.post.caption ?? t(msg`Mira esta publicación en FamiDex`),
      url: props.shareUrl,
    };
    try {
      if (typeof navigator.share === "function") {
        await navigator.share(shareData);
      } else {
        await copyLink();
      }
    } catch {
      /* usuario canceló: no-op */
    }
  };

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      presentation="sheet"
      contentClass="max-w-md"
    >
      <DialogHeader>
        <DialogTitle class="text-lg font-semibold text-foreground">{t(msg`Compartir`)}</DialogTitle>
      </DialogHeader>

      <div class="flex flex-col gap-5 p-4">
        <Show when={showStory()}>
          {/* Previsualización de historia 9:16 */}
          <div
            data-testid="story-preview"
            class="relative mx-auto aspect-[9/16] w-48 overflow-hidden rounded-2xl bg-primary shadow-lg"
          >
            <Show
              when={props.post.cover}
              fallback={<div class="absolute inset-0 bg-gradient-to-b from-primary to-accent" />}
            >
              <img
                src={props.post.cover ?? ""}
                alt=""
                class="absolute inset-0 h-full w-full object-cover"
              />
            </Show>
            <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div class="absolute inset-x-0 bottom-0 p-3 text-primary-foreground">
              <p class="text-xs font-bold uppercase tracking-widest opacity-90">FamiDex</p>
              <p class="line-clamp-2 text-sm font-semibold">{props.post.caption}</p>
              <p class="mt-1 text-[10px] opacity-80">@{props.post.authorName ?? "coleccionista"}</p>
            </div>
          </div>
        </Show>

        <div class="grid grid-cols-3 gap-3">
          <button
            type="button"
            data-testid="share-copy-link"
            onClick={() => void copyLink()}
            class="flex flex-col items-center gap-2 rounded-xl p-2 text-xs font-semibold text-muted-foreground hover:text-primary"
          >
            <span class="grid h-14 w-14 place-items-center rounded-full bg-accent text-accent-foreground">
              <Show when={copied()} fallback={<LinkIcon class="h-6 w-6" />}>
                <Check class="h-6 w-6" />
              </Show>
            </span>
            {copied() ? t(msg`¡Copiado!`) : t(msg`Copiar enlace`)}
          </button>

          <button
            type="button"
            data-testid="share-native"
            onClick={() => void nativeShare()}
            class="flex flex-col items-center gap-2 rounded-xl p-2 text-xs font-semibold text-muted-foreground hover:text-primary"
          >
            <span class="grid h-14 w-14 place-items-center rounded-full bg-accent text-accent-foreground">
              <Share2 class="h-6 w-6" />
            </span>
            {t(msg`Compartir`)}
          </button>

          <button
            type="button"
            data-testid="share-story"
            onClick={() => setShowStory((v) => !v)}
            class="flex flex-col items-center gap-2 rounded-xl p-2 text-xs font-semibold text-muted-foreground hover:text-primary"
          >
            <span class="grid h-14 w-14 place-items-center rounded-full bg-accent text-accent-foreground">
              <Camera class="h-6 w-6" />
            </span>
            {t(msg`Historia`)}
          </button>
        </div>

        <p class="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
          <MessageCircle class="h-3.5 w-3.5" />
          {t(msg`Comparte tu colección con otros amantes de Sylvanian`)}
        </p>
      </div>
    </Dialog>
  );
}
